import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Popover,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { VariableSizeList as _VariableSizeList } from "react-window";
import { VariableSizeListProps } from "react-window";

import { SKILLS, WORKER_TYPES } from "./constants";
import { useFilterData } from "./controllers/use-filter-data";
import FilterSearch from "./filter-accordion/filter-search";
import { Filter } from "./filter-accordion/types";
import FilterListCheckbox from "./filter-accordion/ui/filter-list-checkbox";
import { HierarchicalFilterList } from "./filter-accordion/ui/hierarchical-filter-list";
import { ListForFilter } from "./filter-accordion/ui/list-for-filter";
import { AppliedFilter, FilterData } from "./filter-context";
import { getFilterLabelKey } from "./filter-label-key";

const VariableSizeList = _VariableSizeList as unknown as React.ComponentType<VariableSizeListProps>;

interface FilterPopoverProps {
  filter: Filter;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  appliedFilter: AppliedFilter | undefined;
  onApply: (type: string, data: FilterData[], key?: string) => void;
}

const FilterPopover: React.FC<FilterPopoverProps> = ({
  filter,
  anchorEl,
  onClose,
  appliedFilter,
  onApply,
}) => {
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const [localSelectedItems, setLocalSelectedItems] = useState<FilterData[]>(
    appliedFilter?.data || []
  );
  // Track items that were applied when popover opened, so they stay visible in Applied tab even when unchecked
  const [appliedAtOpen, setAppliedAtOpen] = useState<FilterData[]>(
    appliedFilter?.data || []
  );

  React.useEffect(() => {
    setLocalSelectedItems(appliedFilter?.data || []);
    setAppliedAtOpen(appliedFilter?.data || []);
  }, [appliedFilter]);

  // Reset appliedAtOpen when popover opens
  React.useEffect(() => {
    if (anchorEl) {
      setAppliedAtOpen(appliedFilter?.data || []);
    }
  }, [anchorEl, appliedFilter?.data]);

  const [searchedValue, setSearchedValue] = useState("");
  const { isWorkerSubTypeEnabled } = useFilterData();

  const open = Boolean(anchorEl);

  // Only render as a parent/child tree when the data actually carries children.
  // A flat list (e.g. Skills on the Job Roles page) must render flush-left,
  // without the child indentation the hierarchical list applies.
  const hasChildren = (Array.isArray(filter.data) ? filter.data : []).some(
    (item: FilterData) => item.parentId != null
  );
  const isHierarchical =
    (filter.name === SKILLS ||
      (filter.name === WORKER_TYPES && isWorkerSubTypeEnabled)) &&
    hasChildren;

  const filteredItems = useMemo(() => {
    let items = Array.isArray(filter.reducedData) ? filter.reducedData : filter.data;
    if (searchedValue) {
      if (isHierarchical) {
        const lowerText = searchedValue.toLowerCase();
        const matchedParents = items.filter(
          (item: FilterData) => !item.parentId && item.name.toLowerCase().includes(lowerText)
        );
        const matchedParentIds = new Set(matchedParents.map((d: FilterData) => d.id));
        const childrenOfMatched = items.filter(
          (item: FilterData) => item.parentId != null && matchedParentIds.has(item.parentId)
        );
        items = [...matchedParents, ...childrenOfMatched];
      } else {
        items = items.filter((item: FilterData) =>
          item.name.toLowerCase().includes(searchedValue.toLowerCase())
        );
      }
    }
    if (tabValue === 1) {
      // Applied tab: show items that were applied when opened OR currently selected
      // For hierarchical filters, also include children of applied parents
      const appliedAtOpenIds = new Set(appliedAtOpen.map((d) => d.id));
      if (isHierarchical) {
        // Collect parent IDs from appliedAtOpen only
        const appliedParentIds = new Set(
          appliedAtOpen
            .filter((item: FilterData) => !item.parentId)
            .map((item) => item.id)
        );
        items = items.filter((item: FilterData) =>
          appliedAtOpenIds.has(item.id) ||
          (item.parentId != null && appliedParentIds.has(item.parentId))
        );
      } else {
        items = items.filter((item: FilterData) =>
          appliedAtOpenIds.has(item.id)
        );
      }
    }
    // Sort items alphabetically by name
    if (isHierarchical) {
      // Sort parents and children separately to maintain hierarchy
      const parents = items.filter((item: FilterData) => !item.parentId);
      const children = items.filter((item: FilterData) => item.parentId);
      parents.sort((a: FilterData, b: FilterData) => a.name.localeCompare(b.name));
      children.sort((a: FilterData, b: FilterData) => a.name.localeCompare(b.name));
      items = [...parents, ...children];
    } else {
      items = [...items].sort((a: FilterData, b: FilterData) => a.name.localeCompare(b.name));
    }
    return items;
  }, [filter.data, filter.reducedData, searchedValue, tabValue, isHierarchical, appliedAtOpen]);

  // Track pending apply so we can call onApply outside of setState
  const pendingApplyRef = useRef<FilterData[] | null>(null);

  useEffect(() => {
    if (pendingApplyRef.current !== null) {
      const data = pendingApplyRef.current;
      pendingApplyRef.current = null;
      onApply(filter.name, data, filter.key);
    }
  }, [localSelectedItems, onApply, filter.name, filter.key]);

  const handleToggleItem = (id: number, checked: boolean) => {
    setLocalSelectedItems((prev) => {
      let newSelected: FilterData[];
      if (checked) {
        const sourceData = Array.isArray(filter.reducedData) ? filter.reducedData : filter.data;
        const itemToAdd = sourceData.find((item: FilterData) => item.id === id);
        if (itemToAdd) {
          newSelected = [...prev, itemToAdd];
        } else {
          newSelected = prev;
        }
      } else {
        // For hierarchical filters, also remove all children of the unchecked parent
        const sourceData = Array.isArray(filter.reducedData) ? filter.reducedData : filter.data;
        const childIds = new Set<number | string>();
        const collectChildIds = (parentId: number | string) => {
          sourceData.forEach((item: FilterData) => {
            if (item.parentId === parentId) {
              childIds.add(item.id);
              collectChildIds(item.id);
            }
          });
        };
        collectChildIds(id);
        newSelected = prev.filter((item) => item.id !== id && !childIds.has(item.id));
      }
      pendingApplyRef.current = newSelected;
      return newSelected;
    });
  };

  const handleClear = () => {
    pendingApplyRef.current = [];
    setLocalSelectedItems([]);
  };

  // The "All <filter>" row acts on the items currently visible in the list
  // (respects the active search and cascade-reduced options). It renders as a
  // normal filter option (checked when every visible item is selected) and
  // toggles select-all / deselect-all on click.
  const allVisibleSelected = useMemo(() => {
    if (filteredItems.length === 0) return false;
    const selectedIds = new Set(localSelectedItems.map((item) => item.id));
    return filteredItems.every((item: FilterData) => selectedIds.has(item.id));
  }, [filteredItems, localSelectedItems]);

  // Select-all row label like "All Locations". The category name is translated
  // for display only (its filter.name identifier is untouched) and interpolated
  // into the "All {{name}}" string.
  const allOptionLabel = useMemo(() => {
    const displayName = t(getFilterLabelKey(filter.name), { defaultValue: filter.name });
    return t("filters_all-name", { name: displayName });
  }, [filter.name, t]);

  const handleSelectAll = () => {
    setLocalSelectedItems((prev) => {
      let newSelected: FilterData[];
      if (allVisibleSelected) {
        const visibleIds = new Set(filteredItems.map((item: FilterData) => item.id));
        newSelected = prev.filter((item) => !visibleIds.has(item.id));
      } else {
        const prevIds = new Set(prev.map((item) => item.id));
        const itemsToAdd = filteredItems.filter(
          (item: FilterData) => !prevIds.has(item.id)
        );
        newSelected = [...prev, ...itemsToAdd];
      }
      pendingApplyRef.current = newSelected;
      return newSelected;
    });
  };

  const handleClose = () => {
    setSearchedValue("");
    setTabValue(0);
    onClose();
  };

  const handleApply = () => {
    handleClose();
  };

  const isChecked = (id: number) => {
    return localSelectedItems.some((item) => item.id === id);
  };

  const hasChanged = useMemo(() => {
    const currentData = appliedFilter?.data || [];
    if (currentData.length !== localSelectedItems.length) return true;
    const currentIds = new Set(currentData.map((item) => item.id));
    return localSelectedItems.some((item) => !currentIds.has(item.id));
  }, [appliedFilter?.data, localSelectedItems]);

  const allItems = Array.isArray(filter.reducedData) ? filter.reducedData : filter.data;
  const totalCount = isHierarchical
    ? (allItems?.filter((item: FilterData) => !item.parentId)?.length || 0)
    : (allItems?.length || 0);
  const appliedCount = isHierarchical
    ? appliedAtOpen.filter((item: FilterData) => !item.parentId).length
    : appliedAtOpen.length;

  const listWidth = 320;
  const listHeight = 300;

  // The "All <filter>" row is only rendered on the "All" tab
  const hasAllRow = tabValue === 0;

  // react-window caches item sizes; reset when the first/last item heights
  // can change (tab switch adds/removes the "All" row, search changes length)
  const listRef = useRef<_VariableSizeList>(null);
  useEffect(() => {
    listRef.current?.resetAfterIndex(0, true);
  }, [tabValue, filteredItems.length]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      PaperProps={{
        sx: { width: listWidth, borderRadius: "8px", mt: 1 },
      }}
    >
      <Box sx={{ pt: 2, pl: 2, pr: 2 }}>
        <FilterSearch
          filter={filter}
          handleSearch={(text) => setSearchedValue(text)}
          searchedValue={searchedValue}
          setSearchedValue={setSearchedValue}
        />
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ mt: 1, minHeight: "unset" }}
          variant="fullWidth"
        >
          <Tab label={t("filters_all-count", { count: totalCount })} sx={{ textTransform: "none", minHeight: "36px" }} />
          <Tab label={t("filters_applied-count", { count: appliedCount })} sx={{ textTransform: "none", minHeight: "36px" }} />
        </Tabs>
      </Box>
      <Divider />
      {tabValue === 0 && filteredItems.length > 0 && (
        <div style={{ paddingTop: "12px", overflowX: "hidden" }}>
          <FormControlLabel
            sx={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            control={
              <FilterListCheckbox
                checked={allVisibleSelected}
                onChange={handleSelectAll}
              />
            }
            label={
              <Box
                sx={{
                  fontSize: "14px",
                  fontFamily: "Roboto",
                  fontWeight: 700,
                  marginLeft: "2px",
                }}
              >
                {allOptionLabel}
              </Box>
            }
          />
        </div>
      )}
      <Box sx={{ height: listHeight, overflow: "hidden" }}>
        {filteredItems.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              p: 2,
            }}
          >
            <Typography variant="body2" color="textSecondary">
              {tabValue === 1
                ? t("filters_no-active-filters")
                : t("filters_no-options-found")}
            </Typography>
          </Box>
        ) : isHierarchical ? (
          <HierarchicalFilterList
            items={filteredItems}
            searchedValue={searchedValue}
            isChecked={isChecked}
            applyFilter={handleToggleItem}
            listWidth={listWidth}
            listHeight={listHeight}
          />
        ) : (
          <VariableSizeList
            ref={listRef}
            height={listHeight}
            width="100%"
            itemSize={(index) => {
                // The "All <filter>" row above the list already provides the
                // top spacing, so the first item only needs it when that row
                // is absent (Applied tab)
                if ((index === 0 && !hasAllRow) || index === filteredItems.length - 1) {
                    return 42;
                }
                return 30;
            }}
            itemCount={filteredItems.length}
            overscanCount={5}
            itemData={{
              items: filteredItems,
              searchedValue,
              isChecked,
              applyFilter: handleToggleItem,
              listWidth,
              hasAllRow,
            }}
          >
            {ListForFilter}
          </VariableSizeList>
        )}
      </Box>
      <Divider />
      <Box sx={{ p: 1, display: "flex", justifyContent: "space-between" }}>
        <Button onClick={handleClear} size="small" sx={{ textTransform: "none" }}>
          {t("filters_clear")}
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          size="small"
          sx={{ textTransform: "none" }}
        >
          {t("common_close")}
        </Button>
      </Box>
    </Popover>
  );
};

export default FilterPopover;
